import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Lock, Phone, ArrowUpRight, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import useSeo from "@/hooks/useSeo";

export default function AdminLogin() {
  useSeo({ title: "Admin login — RSR Australia", description: "RSR Australia admin login." });
  const { admin, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (admin) {
    const from = location.state?.from || "/admin";
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-baseline gap-2 mb-10">
          <span className="font-display text-2xl font-extrabold tracking-tight">RSR</span>
          <span className="overline">Australia · Admin</span>
        </div>
        <div className="border border-border bg-card p-7 lg:p-9">
          <div className="w-10 h-10 bg-accent/10 text-accent flex items-center justify-center mb-5">
            <Lock className="w-5 h-5" strokeWidth={1.7} />
          </div>
          <h1 className="font-display text-3xl font-extrabold tracking-tighter">Sign in.</h1>
          <p className="mt-2 text-sm text-muted-foreground">Manage leads, email settings and templates.</p>

          <form onSubmit={onSubmit} className="mt-7 space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-foreground/70">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@rsraustralia.com.au"
                required
                className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-foreground/70">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-2 bg-background border-border focus:border-accent focus-visible:ring-1 focus-visible:ring-accent/30 h-12"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-accent text-white px-5 py-3 font-semibold text-sm hover:bg-accent/90 transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"} <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-7 pt-6 border-t border-border text-xs text-muted-foreground space-y-2">
            <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Forgot the password? Update <code className="bg-secondary px-1.5 py-0.5">ADMIN_PASSWORD</code> in <code>backend/.env</code> and restart.</div>
            <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Not the admin? <a href="/" className="text-accent hover:underline">Back to site</a></div>
          </div>
        </div>
      </div>
    </div>
  );
}
